import {ElementRef, Injectable} from '@angular/core';
import {MenuItem, MenuItemPush, PackagePick, PushPage} from '../models/FSMenu';
import {OrderItem} from '../models/Order';
import {BehaviorSubject, debounceTime, Observable, skip} from 'rxjs';
import { map } from 'rxjs/operators';
import {UrlHistoryService} from './url.history.service';
import {TranslateService} from './translate.service';

export interface BottomSheetData {
  item?: MenuItem;
  itemMode?: string;
  // itemMode "edit": the cart row whose selection is being changed
  editItem?: OrderItem;
  sheetClass?: string;
  pushPage?: PushPage;
  title?: string;
  locked?: boolean;
  sheetData?: any;
}

@Injectable({
  providedIn: 'root'
})
export class BottomSheetService {
  private sheetStackSubject = new BehaviorSubject<BottomSheetData[]>([]);

  constructor(private urlHistory: UrlHistoryService, private translate: TranslateService) {
    this.urlHistory.browserBack$.pipe(
      skip(1),  // The subject's initial value is no back navigation: it would close a sheet opened at start
      debounceTime(50)  // Small buffer for synchronization
    ).subscribe(() => {
      this.closeSheet();
    });
  }

  // Returns the current active sheet (top of stack)
  observeActiveSheet(): Observable<BottomSheetData | null> {
    return this.sheetStackSubject.pipe(
      map(stack => stack.length > 0 ? stack[stack.length - 1] : null)
    );
  }

  // Returns the entire stack
  observeSheetStack(): Observable<BottomSheetData[]> {
    return this.sheetStackSubject;
  }

  // Push a new sheet onto the stack
  pushSheet(sheetData: BottomSheetData) {
    const currentStack = this.sheetStackSubject.getValue();
    this.sheetStackSubject.next([...currentStack, sheetData]);
  }

  // Remove the top sheet from the stack
  popSheet(count: number = 1): BottomSheetData | null {
    const currentStack = this.sheetStackSubject.getValue();
    if (currentStack.length === 0) return null;
    console.log("popSheet", count);
    console.log("oldStack", currentStack);

    const newStackLength = Math.max(currentStack.length - count, 0);
    const newStack = currentStack.slice(0, newStackLength);
    const poppedSheet = currentStack[currentStack.length - 1];
    console.log("newStack", newStack);

    this.sheetStackSubject.next(newStack);
    return poppedSheet;
  }

  replaceSheet(sheetData: BottomSheetData) {
    const currentStack = this.sheetStackSubject.getValue();
    if (currentStack.length === 0) {
      this.pushSheet(sheetData);
    } else {
      const newStack = [...currentStack.slice(0, -1), sheetData];
      this.sheetStackSubject.next(newStack);
    }
  }

  // Remove all sheets
  clearStack() {
    this.sheetStackSubject.next([]);
  }

  // Replace the entire stack with a new one
  setStack(stack: BottomSheetData[]) {
    this.sheetStackSubject.next([...stack]);
  }

  // Get the number of sheets in the stack
  getStackSize(): number {
    return this.sheetStackSubject.getValue().length;
  }

  // Check if stack is empty
  isEmpty(): boolean {
    return this.getStackSize() === 0;
  }

  displayItem(item: MenuItem, mode: string = "add", editItem?: OrderItem) {
    this.pushSheet({item: item, title: item.name, itemMode: mode, editItem});
  }

  // The item view in pick mode: the guest chooses a package's options and `pick.done` gets them
  displayPackagePick(item: MenuItem, pick: PackagePick) {
    this.pushSheet({item: item, title: item.name, itemMode: "pick", sheetData: pick});
  }

  displaySignin(popCount: number = 1) {
    console.log("popCount", popCount)
    this.displaySheetClass("sign-in", this.getString("Verifiera"), { popCount });
  }

  displayPushPage(pushPage: PushPage) {
    const pi = pushPage.item.pushes[pushPage.pushIndex];
    this.replaceSheet({pushPage, title: this.getString(pi.title)});
  }

  displaySheetClass(sheetClass: string, title: string, sheetData?: any) {
    this.pushSheet({sheetClass, title, sheetData});
  }

  lockSheet() {
    // Mark the current sheet as locked
    const currentSheet = this.sheetStackSubject.getValue();
    if (currentSheet.length > 0) {
      currentSheet[currentSheet.length - 1].locked = true;
    }
  }

  closeSheet() {
    // Close the current sheet if it is not locked
    const currentSheet = this.sheetStackSubject.getValue();
    if (currentSheet.length > 0 && !currentSheet[currentSheet.length - 1].locked) {
      this.popSheet();
    }
  }

  private getString(s: string) {
    return this.translate.getString(s);
  }
}
